import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import ConversationEntry from './ConversationEntry';
import { ConversationCategory } from '../types/conversation';
import JumpToLatestButton from './ui/JumpToLatestButton';
import LiveKnowledgeSearch from './LiveKnowledgeSearch';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';

const ConversationFeed: React.FC = () => {
  const { entries } = useSelector((state: RootState) => state.conversation);
  const feedRef = useRef<HTMLDivElement>(null);

  // New Inquiries are displayed in the top NewInquiryBar — exclude them here
  const feedEntries = entries.filter(
    (e) => e.category !== ConversationCategory.NEW_INQUIRY
  ).slice().reverse();

  // Jump back to the newest entry (the list is newest first)
  const scrollToLatest = () => {
    if (feedRef.current) {
      feedRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // The macOS insights panel: white, radius 16, no border or shadow.
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card bg-surface">
      {/* Header on signature-tint: the title and count, Search KB, and the arrow that scrolls
          this list back to the newest, over a signature rule at 30%. Search KB sits here on
          macOS and WPF too. */}
      <div
        className="flex flex-shrink-0 items-center gap-2 border-b bg-signature-tint px-3 py-2"
        style={{ borderBottomColor: 'color-mix(in srgb, var(--signature) 30%, transparent)' }}
      >
        <ForumOutlinedIcon aria-hidden="true" className="flex-shrink-0 text-ink" sx={{ fontSize: 12 }} />
        <h2 className="min-w-0 truncate text-sm font-semibold text-ink">Live Call Insights</h2>
        <span className="text-[11px] text-ink-muted">{feedEntries.length}</span>
        <span className="flex-1" />
        <LiveKnowledgeSearch />
        {feedEntries.length > 0 && <JumpToLatestButton onClick={scrollToLatest} />}
      </div>

      {/* Arrivals never move the reader's scroll position. */}
      <div className="flex min-h-0 flex-1 flex-col">
        <div
          ref={feedRef}
          className="min-h-0 flex-1 overflow-y-auto p-3 space-y-2"
          style={{ scrollBehavior: 'smooth' }}
        >
          {feedEntries.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="space-y-3 text-center">
                <h3 className="text-sm font-semibold text-ink">No conversations detected yet</h3>
                <p className="text-[13px] text-ink-muted">Start a conversation and questions will appear here automatically</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {feedEntries.map((entry) => (
                <ConversationEntry key={entry.id} entry={entry} />
              ))}
              <div className="h-3" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationFeed;
